/* eslint-disable import/extensions */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
    SwingTimingData,
    TriggerSwingTimingVisualEvent,
    GroupByType,
} from 'leaderboard/scripts/swing-path/types/globals.ts'
import { TRIGGER_SWING_TIMING_VISUAL_ADD } from '../leaderboard/utils/event-constants.ts'
import SwingTimingChart from './swing-timing-chart.ts'
import PlayerSearch from './PlayerSearch.tsx'
import Legend from './Legend.tsx'
import Controls from './Controls.tsx'
import ChartWrapper from './ChartWrapper.tsx'
import { parseUniqueId } from './key-parsing.ts'
import doGroupByLogic from './utils/do-group-by-logic.ts'
import { MAX_SWING_TIMING_SELECTIONS, MIN_SWINGS } from './utils/constants.ts'
import {
    getPitchTypeColorModeByUniqueId,
    type PitchTypeColorMode,
} from './utils/can-use-pitch-type-colors.ts'

type Handedness = 'L' | 'R'
export type HandednessWithCombination = Handedness | 'X'

export type DistributionBin = {
    api_pitch_type_group03?: string
    api_pitch_type_group09?: string
    pitch_type?: string
    pitch_hand?: HandednessWithCombination
    bat_side?: HandednessWithCombination
    year?: number
    bin: number
    n: number
    avg_run_exp: number
}

export type AugmentedBin = DistributionBin & {
    n_pct: number
}
export type DistributionObj = {
    x: DistributionBin[]
    y: DistributionBin[]
    z: DistributionBin[]
}

export type Dimension = 'x' | 'y' | 'z'

// Makes my head spin less for debugging
// const dimensions: Dimension[] = ['x']

type WrapperProps = {
    data: SwingTimingData[]
    charts: SwingTimingChart[]
    groupBys: string[]
    type: GroupByType
}

export function getKeyFromRow(row: SwingTimingData) {
    return row.uniqueId
}

function addPitcherData(
    binnedData: DistributionObj,
    charts: SwingTimingChart[],
    row: SwingTimingData
) {
    charts.forEach((chart) => {
        chart.addRawerData(binnedData, row)
    })
}

function removePitcherData(row: SwingTimingData, charts: SwingTimingChart[]) {
    charts.forEach((chart) => {
        chart.removeByRow(row)
    })
}

const fetchBinnedData = async (
    row: SwingTimingData,
    groupBys: string[]
): Promise<DistributionObj> => {
    const urlParams = new URLSearchParams(window.location.search)

    const unique = parseUniqueId(row.uniqueId, groupBys)

    Object.keys(unique).forEach((key) => {
        urlParams.set(key, String(unique[key]))
    })

    if (!urlParams.get('split[]')) {
        serverParams.secondarySplit.forEach((split) => {
            urlParams.append('split[]', split)
        })
    }

    return fetch(
        `/leaderboard/bat-tracking/swing-timing-distribution/player/${row.id}?${urlParams}`
    ).then((res) => res.json())
}

export default function Wrapper(props: WrapperProps) {
    const { charts, data, groupBys, type } = props
    const [selectedRows, setSelectedRows] = useState<SwingTimingData[]>([])

    const selectedRowsRef = useRef(selectedRows)
    const inFlightAddsRef = useRef(0)
    // Trying to make things easier to grok, not sure
    useEffect(() => {
        selectedRowsRef.current = selectedRows
    }, [selectedRows])

    const visualRef = useRef<HTMLDivElement>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [scaleType, setScaleType] = useState<'n' | 'n_pct'>('n_pct')
    const [loadingUniqueIds, setLoadingUniqueIds] = useState<Set<string>>(
        new Set()
    )
    const [showLeagueAverages, setShowLeagueAverages] = useState(false)
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)

    const handleScaleTypeChange = useCallback(
        async (newScaleType: 'n' | 'n_pct') => {
            if (isTransitioning || isLoading) return

            setIsTransitioning(true)
            setScaleType(newScaleType)

            await Promise.all(
                charts.map((chart) => chart.updateScaleType(newScaleType))
            )

            // Auto-hide league averages when switching to raw
            if (newScaleType === 'n' && showLeagueAverages) {
                setShowLeagueAverages(false)
                charts.forEach((chart) => chart.setLeagueAverageStatus(false))
            }

            setIsTransitioning(false)
        },
        [isTransitioning, isLoading, showLeagueAverages]
    )

    const handleShowLeagueAveragesChange = useCallback(
        (show: boolean) => {
            if (isTransitioning || isLoading) return
            setShowLeagueAverages(show)
            charts.forEach((chart) => chart.setLeagueAverageStatus(show))
        },
        [isTransitioning, isLoading]
    )
    const [hasUserInteracted, setHasUserInteracted] = useState(false)

    const groupByLogic = doGroupByLogic(groupBys)
    const pitchTypeColorMode: PitchTypeColorMode = useMemo(
        () =>
            getPitchTypeColorModeByUniqueId(
                selectedRows.map((row) => row.uniqueId),
                groupBys
            ),
        [selectedRows, groupBys]
    )
    const usePitchTypeColors = pitchTypeColorMode !== 'none'

    const clearRowLoading = useCallback((uniqueId: string) => {
        setLoadingUniqueIds((prev) => {
            if (!prev.has(uniqueId)) {
                return prev
            }

            const next = new Set(prev)
            next.delete(uniqueId)
            return next
        })
    }, [])

    // Check all the reasons we might not want to add a row
    const getRowsToAdd = useCallback((rows: SwingTimingData[]) => {
        const existingIds = new Set(
            selectedRowsRef.current.map((row) => row.uniqueId)
        )
        const availableSlots =
            MAX_SWING_TIMING_SELECTIONS - selectedRowsRef.current.length

        // no slots available
        if (availableSlots <= 0) {
            return [] as SwingTimingData[]
        }

        const rowsToAdd: SwingTimingData[] = []

        rows.forEach((row) => {
            // filled
            if (rowsToAdd.length >= availableSlots) {
                return
            }

            // already in chart
            if (existingIds.has(row.uniqueId)) {
                return
            }

            // by going iteratively we catch the case where we're passed multiple ids of the same uniqueId
            existingIds.add(row.uniqueId)
            rowsToAdd.push(row)
        })

        return rowsToAdd
    }, [])

    const addRows = useCallback((rowsToAdd: SwingTimingData[]) => {
        if (!rowsToAdd.length) {
            return
        }

        const nextSelectedRows = [...selectedRowsRef.current, ...rowsToAdd]
        selectedRowsRef.current = nextSelectedRows
        setSelectedRows(nextSelectedRows)
    }, [])

    // TODO: We good here?
    const handleRemove = useCallback(
        async (row: SwingTimingData) => {
            setHasUserInteracted(true)

            charts.forEach((chart) => {
                chart.setHoveredCurve(null)
            })
            setHoveredKey(null)

            const newSelected = selectedRowsRef.current.filter((r) => r !== row)
            selectedRowsRef.current = newSelected
            setSelectedRows(newSelected)
            clearRowLoading(row.uniqueId)

            removePitcherData(row, charts)
        },
        [charts, clearRowLoading]
    )

    // Handle adding from table button
    useEffect(() => {
        function handleTriggerVisual(e: TriggerSwingTimingVisualEvent) {
            const row = data.find((d) => d.uniqueId === e.detail.uniqueId)

            if (!row) {
                throw new Error("Couldn't find row")
            }

            handleSearchAdd(row)

            if (visualRef.current) {
                visualRef.current.scrollIntoView({
                    behavior: 'smooth',
                })
            }
        }

        window.addEventListener(
            TRIGGER_SWING_TIMING_VISUAL_ADD,
            handleTriggerVisual as EventListener
        )

        return () =>
            window.removeEventListener(
                TRIGGER_SWING_TIMING_VISUAL_ADD,
                handleTriggerVisual as EventListener
            )
    }, [handleSearchAdd])

    function getInitialUniqueIdsFromUrl(): string[] {
        const params = new URLSearchParams(window.location.search)
        const ids = params.getAll('id') || []

        return [...new Set(ids.map((d) => d.trim()).filter(Boolean))].slice(
            0,
            MAX_SWING_TIMING_SELECTIONS
        )
    }

    // Keep url in sync
    useEffect(() => {
        if (!hasUserInteracted) {
            return
        }

        const url = new URL(window.location.href)
        const params = url.searchParams

        params.delete('id')
        selectedRows.forEach((row) => {
            params.append('id', row.uniqueId)
        })

        const next = `${url.pathname}${
            params.toString() ? `?${params.toString()}` : ''
        }${url.hash}`
        window.history.replaceState('', '', next)
    }, [selectedRows, hasUserInteracted])

    // Initial Load
    useEffect(() => {
        if (!charts.length) {
            return
        }

        // Setup curve hover handlers
        charts.forEach((chart) => {
            chart.setOnCurveHover(setHoveredKey)
        })

        const initialUniqueIds = getInitialUniqueIdsFromUrl()

        const initialRows = initialUniqueIds
            .map((id) => data.find((d) => d.uniqueId === id))
            .filter((d): d is SwingTimingData => Boolean(d))

        const firstValid = data.find((d) => d.n_swings >= MIN_SWINGS)

        if (!firstValid) {
            return
        }

        const rowsToAdd = initialRows.length ? initialRows : [firstValid]

        handleBulkAddData(rowsToAdd)
    }, [charts])

    // Take an array of rows to add
    async function handleBulkAddData(rows: SwingTimingData[]) {
        // Add to selectedRows
        addRows(rows)

        handleLoadingStart(rows.length)

        // Keep track of current loading rows
        setLoadingUniqueIds((prev) => {
            const next = new Set(prev)
            rows.forEach((row) => {
                next.add(row.uniqueId)
            })
            return next
        })

        // Fetch All Data
        const allData = await Promise.all(
            rows.map((row) => fetchBinnedData(row, groupBys))
        )

        allData.forEach((binnedData, i) => {
            addPitcherData(binnedData, charts, rows[i])
        })

        // Keep track of current loading rows
        setLoadingUniqueIds((prev) => {
            const next = new Set(prev)
            rows.forEach((row) => {
                next.delete(row.uniqueId)
            })
            return next
        })

        handleLoadingEnd(rows.length)
    }

    function handleLoadingStart(len: number) {
        // Set is Loading, track number
        inFlightAddsRef.current += len
        setIsLoading(true)

        charts.forEach((chart) => {
            chart.setLoading(true)
        })
    }

    function handleLoadingEnd(len: number) {
        // Set is loading
        inFlightAddsRef.current -= len
        inFlightAddsRef.current = Math.max(0, inFlightAddsRef.current) // overly cautious, at least for now

        if (inFlightAddsRef.current === 0) {
            setIsLoading(false)

            charts.forEach((chart) => {
                chart.setLoading(false)
            })
        }
    }

    async function handleSearchAdd(row: SwingTimingData) {
        // Set hasInteracted
        setHasUserInteracted(true)

        // Add to selectedRows
        const rowsToAdd = getRowsToAdd([row])

        handleBulkAddData(rowsToAdd)
    }

    return (
        <div ref={visualRef}>
            <div
                className="savant-viz-container"
                style={{ fontFamily: 'Roboto Condensed' }}
            >
                <Legend
                    handleClick={handleRemove}
                    rows={selectedRows}
                    groupByLogic={groupByLogic}
                    type={type}
                    groupBys={groupBys}
                    showLeagueAverages={showLeagueAverages}
                    pitchTypeColors={usePitchTypeColors}
                    pitchTypeColorMode={pitchTypeColorMode}
                    hoveredKey={hoveredKey}
                    charts={charts}
                    loadingUniqueIds={loadingUniqueIds}
                />
                <ChartWrapper
                    rows={selectedRows}
                    type={type}
                    pitchTypeColors={usePitchTypeColors}
                    pitchTypeColorMode={pitchTypeColorMode}
                    hoveredKey={hoveredKey}
                    handleRemove={handleRemove}
                    groupByLogic={groupByLogic}
                    charts={charts}
                />
            </div>
            <Controls
                isDisabled={isLoading || isTransitioning}
                scaleType={scaleType}
                onScaleTypeChange={handleScaleTypeChange}
                showLeagueAverages={showLeagueAverages}
                onShowLeagueAveragesChange={handleShowLeagueAveragesChange}
            />
            <PlayerSearch
                data={data}
                onAdd={(row) => handleSearchAdd(row)}
                selectedUniqueIds={selectedRows.map((row) => row.uniqueId)}
                isFull={selectedRows.length >= MAX_SWING_TIMING_SELECTIONS}
                groupBys={groupBys}
                groupByLogic={groupByLogic}
                type={type}
            />
        </div>
    )
}
